import React from 'react';
import Tippy from '@tippyjs/react';
import './MessageBubble.scss';
import { TranscriptMessage } from '../../shared/types';
import { getCategoryDisplayName } from '../../shared/categoryColors';
import {
  getMessageSentimentClass,
  getMessageSentimentTooltip,
} from '../../shared/messageSentiment';

interface MessageBubbleProps {
  message: TranscriptMessage;
  firstInGroup: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, firstInGroup }) => {
  const isSystem = message.emitterRole === 'System';
  const time = message.chatFields?.msgTime ?? '';

  const primaryCategory = message.categories?.[0];
  const catName = primaryCategory?.name ?? primaryCategory?.id ?? 'Uncategorized';
  const catDisplayName = getCategoryDisplayName(catName);
  const sentiment = message.sentimentCategory ?? message.sentiment;
  const sentimentTooltip = getMessageSentimentTooltip(sentiment);

  if (isSystem) {
    return (
      <div className="ca-msg ca-msg--system" role="listitem">
        <span className="ca-msg__system-text">{message.verbatim ?? ''}</span>
        {time && <span className="ca-msg__system-time">{time}</span>}
      </div>
    );
  }

  const isCustomer = message.emitterRole === 'Customer';
  const isBot = message.emitterRole === 'Bot';
  const isAgent = message.emitterRole === 'Agent';
  const isResponder = isAgent || isBot || !isCustomer;
  const bubbleBg = isResponder ? '#EDF0F4' : '#E6F1EC';
  const avatarBg = isResponder ? '#003D5B' : '#0A6B79';
  const avatarLetter = isAgent ? 'A' : isBot ? 'B' : 'C';
  const roleName = isAgent ? 'Agent' : isBot ? 'Chat Bot' : isCustomer ? 'Customer' : 'Responder';
  const side = isResponder ? 'agent' : 'customer';

  return (
    <div className={`ca-msg ca-msg--${side}`} role="listitem">
      {isResponder && (
        <div className="ca-msg__avatar-slot">
          {firstInGroup && (
            <span className="ca-msg__avatar" style={{ background: avatarBg }} aria-hidden="true">
              {isAgent || isBot ? (
                avatarLetter
              ) : (
                <span className="material-icons">person_outline</span>
              )}
            </span>
          )}
        </div>
      )}

      <div className={`ca-msg__col ca-msg__col--${side}`}>
        {firstInGroup && <div className={`ca-msg__role ca-msg__role--${side}`}>{roleName}</div>}
        <div
          className={`ca-msg__bubble ca-msg__bubble--${side}${
            firstInGroup ? ' ca-msg__bubble--first' : ''
          }`}
          style={{ background: bubbleBg }}
        >
          {firstInGroup && (
            <span
              className={`ca-msg__tail ca-msg__tail--${side}`}
              style={{
                borderColor: isResponder
                  ? `transparent ${bubbleBg} transparent transparent`
                  : `transparent transparent transparent ${bubbleBg}`,
              }}
            />
          )}
          {message.verbatim ?? ''}
        </div>
        <div className={`ca-msg__meta ca-msg__meta--${side}`}>
          <span className="ca-msg__cat">
            <Tippy
              className="ca-session-list-tooltip"
              content={sentimentTooltip}
              placement="top"
              zIndex={100000}
            >
              <span
                className={`ca-msg__sentiment ${getMessageSentimentClass(sentiment)}`}
                aria-label={sentimentTooltip}
                role="img"
              />
            </Tippy>
            {catDisplayName}
          </span>
          {time && <span className="ca-msg__time">{time}</span>}
        </div>
      </div>

      {!isResponder && (
        <div className="ca-msg__avatar-slot">
          {firstInGroup && (
            <span className="ca-msg__avatar" style={{ background: avatarBg }} aria-hidden="true">
              {avatarLetter}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
